import { graphql, useStaticQuery } from "gatsby";
import React from "react";

import { useTheme } from "../../context/ThemeContext";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { Link } from "../../utils/customLink";
import { formatCurrencyAmount } from "../../utils/formatContent";
import { flexConfig } from "../../utils/sharedDatasets";
import Image from "../image";
import TravelFlexibility from "../program/travelFlexibility";
import CtaButton from "../shared/buttons/ctaButton";
import CollapsibleListINGOWrapper from "../shared/collapsibleListINGOWrapper";
import ProgramCapacity from "../shared/programCapacity";
import ProgramRating from "../shared/programRating";
import ViewButton from "../shared/viewButton";
import GviFlexIconStrip from "./gviFlexIconStrip";

const ProgramApplyCard = ({
  program,
  buttonClassName,
  labelClass,
  arrowClass,
  descWidth = "w-2/3",
  type = "apply",
  target = "application",
  ctaApplyLabel = "Apply",
  ctaViewLabel = "Visit the program",
  ctaViewPreset = "secondary",
  ctaViewExtraClass = "",
  ctaViewLabelClass = "",
  ctaViewArrowClass = "",
  cardClickable = false,
  groupProgram = false,
  isProgramFocusPage = false,
  featuredIncludes = [],
  containerClass = "relative w-full mb-6 md:mb-10 rounded-sm sm:rounded-md overflow-hidden border-2 hover:border-3 bg-black-lighter planet:bg-transparent",
  mainProgramClass = "text-mob md:text-base leading-genericCta font-bold md:font-normal text-left text-generic-text planet:text-white sm:min-h-0 sm:h-auto",
  travelInnerContainerClass = "",
  programRatingContainerClass = "justify-center items-center pt-0 w-full hidden sm:flex",
  travelHeadingClass = "",
  travelDetailsTitle,
  travelDescriptionClass = "",
  travelDetailsDescriptionClass = "",
  programLogo = "",
  rightContainerSectionClass = "",
  cardClass = "sm:flex-row",
  programTypeClass = "",
  programContainerClass = "",
  programTitleClass = "",
  runByTextClass = "",
  ledByNameClass = "",
  itemTextClass = "",
  durationClass = "pr-0 xxs:text-sm sm:text-base",
  priceClass = "font-body font-normal xxs:text-sm sm:text-base",
  runAndLedByGVi,
  runAndLedByFlex,
  extLink = false,
  showProgramRating = true
}) => {
  const { genericDetails } = useStaticQuery(graphql`
    query {
      genericDetails: wp {
        globalProgramConfig: gviConfigSettings {
          acfGenericGlobalConfig {
            ...StickyWidgetFragment
          }
        }
      }
    }
  `);

  const breakpoint = useBreakpoint();
  const { currentSiteMeta } = useTheme();

  const currencyToUse = currentSiteMeta?.currency
    ? currentSiteMeta?.currency?.toLowerCase()
    : "gbp";

  // console.debug("programApplyCard :: program", {
  //   program
  // });

  const programType =
    program?.types?.map((type) => type) ??
    program?.acfProgramsMeta?.programTypes.map((type) => type.title);

  const programTypeSlugs =
    program?.types?.map((type) => type) ??
    program?.acfProgramsMeta?.programTypes.map((type) => type.slug);

  const programHeaderSubsectionTitle =
    program?.subtitle ??
    program?.acfProgramsMeta?.programHeaderSubsectionTitle ??
    "";

  const programIngo =
    program?.programIngo ?? program?.acfProgramsMeta?.programIngo;
  const programJourneys =
    program?.programJourneys ||
    program?.acfProgramsMeta?.programJourneys ||
    /journey/gi.test(programType);

  const programIdentifier = program?.programCode ?? program?.id;
  const programSuffix = /[a-zA-Z]/.test(programIdentifier)
    ? ["program_code", programIdentifier].join("=")
    : ["program_id", programIdentifier].join("=");

  const to =
    target === "application"
      ? groupProgram
        ? `/group-application-form/?type=application&${programSuffix}&program_title=${program?.title}`
        : programJourneys
          ? `/application-form/?journey_program&${programSuffix}`
          : programIngo
            ? `/application-form/?flex_program&${programSuffix}`
            : `/application-form/?${programSuffix}`
      : program?.link ?? program?.uri;

  const programPopular =
    program?.programPopular ??
    program?.acfProgramsMeta?.programPopular ??
    false;
  const programCapacity =
    program?.programCapacity ?? program?.acfProgramsMeta?.programCapacity ?? "";

  const { bookWithConfidenceTitle, bookWithConfidenceText } =
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? {};

  const travelDetails = {
    title: bookWithConfidenceTitle ?? "Book with confidence!",
    description:
      breakpoint === "xs"
        ? ``
        : `<span class="${travelDetailsDescriptionClass}">Modify your booking, even up to 48 hours before departure. <a  class='underline inline-block' href='/terms-and-conditions' rel='nofollow' target='_gviblank'>T&Cs apply.</a></span>`
  };

  const ingoFeaturedWhatsincluded =
    program?.ingoFeaturedWhatsincluded ??
    program?.acfProgramsMeta?.ingoFeaturedWhatsincluded;

  const currentMinPriceFilter =
    program.acfProgramsMeta?.minimumPriceFilter?.find(
      (filter) =>
        filter?.maxPriceFilterCurrencyIsoCode?.toLowerCase() === currencyToUse
    );

  const minPriceFilterAmount = programIngo
    ? currentMinPriceFilter?.maxPriceFilterDailyAmount ??
      currentMinPriceFilter?.maxPriceFilterAmount ??
      program?.minimumPriceAmount ??
      program?.minimumPrice ??
      ""
    : currentMinPriceFilter?.maxPriceFilterAmount ??
      program?.minimumPriceAmount ??
      program?.minimumPrice ??
      "";

  const minPrice =
    formatCurrencyAmount({ currentSiteMeta, amount: minPriceFilterAmount }) ??
    "";

  let filterIncludeslist = programTypeSlugs?.flatMap((type) =>
    featuredIncludes
      ?.filter((item) =>
        groupProgram
          ? item?.types?.includes("study-abroad")
          : item?.types?.includes(type)
      )
      ?.flatMap((list) => list?.features)
  );
  filterIncludeslist = [
    ...new Set(filterIncludeslist.flat().filter((el) => el))
  ];

  const flexFeatureList = ingoFeaturedWhatsincluded?.map((item) => ({
    name: item.title
  }));

  const includes = programIngo ? flexFeatureList : filterIncludeslist;

  const includesList = includes?.map((item, idx) => {
    return {
      name: item.name,
      useKey: `skill-key-${idx}`
    };
  });

  return (
    <>
      <div className={`${containerClass}`}>
        {cardClickable && (
          <Link
            href={to}
            className={
              "faux-clickable-area bg-transparent absolute top-0 left-0 right-0 bottom-0 z-10 block sm:hidden"
            }
            title={""}
          />
        )}
        <div
          className={`w-full card-holder gvi-program-card-box rounded-xs hover:rounded-none flex flex-col ${
            type !== "apply" ? "sm:h-full" : ""
          } 
         ${cardClass}
          ${cardClickable ? "cursor-pointer" : ""}`}
        >
          <div
            className={`image-wrapper-container 
              ${
                type === "apply"
                  ? "w-full h-56 sm:h-auto sm:w-1/3 relative overflow-hidden"
                  : "relative h-32"
              }`}
          >
            <Image
              imageClass={`object-cover object-center w-full rounded-md h-full  ${
                type === "apply"
                  ? "rounded-b-none md:rounded-l-md md:rounded-r-none"
                  : "md:rounded-t-md md:rounded-b-none"
              }`}
              sourceUrl={
                program?.sourceUrl ?? program?.featuredImage?.node?.sourceUrl
              }
              image={""}
              imageAlt=""
            />

            <div className="absolute z-10 pr-4 pl-3 -top-0.5">
              <div className="overflow-hidden">
                <div
                  className={`bg-black-lighter  text-xs text-center uppercase px-2 py-2 ${programTypeClass}`}
                  dangerouslySetInnerHTML={{
                    __html: /journey/gi.test(programType)
                      ? [programType, " - NEW!"].join("")
                      : programType
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className={`flex w-full sm:w-2/3 flex-col items-center sm:items-start h-full md:px-6 bg-transparent px-4 sm:px-0 rounded-b ${
              type === "apply"
                ? "items-start pb-8 md:py-5"
                : "items-center md:flex-row  md:justify-between py-4"
            } ${programContainerClass}`}
          >
            <div className="flex flex-col md:flex-row h-full md:gap-4 w-full">
              <div className={`sm:w-3/4 flex flex-col justify-between sm:z-10`}>
                <div className="program-description w-full h-full flex flex-col">
                  {showProgramRating && (
                    <div className="program-rating-container justify-center items-center pt-4 w-full sm:hidden">
                      <ProgramRating
                        outerContainerClass={
                          "relative items-center gap-2 w-full"
                        }
                        ratingIconClass={
                          "fill-white planet:fill-generic-text h-4"
                        }
                        ratingLogoClass={
                          "fill-white planet:fill-gray-lighter h-4"
                        }
                        darkRatingIconClass={"block fill-white"}
                        darkContainerClass={"flex gap-2 items-center"}
                        ingoProgram={programIngo}
                      />
                    </div>
                  )}
                  <div className={`md:${descWidth} w-full`}>
                    <div
                      className={`${mainProgramClass}  ${
                        type === "apply" ? "md:font-bold pt-4 md:pt-0" : ""
                      }  ${programTitleClass}`}
                      dangerouslySetInnerHTML={{ __html: program?.title }}
                    />

                    {/journey/gi.test(programType) &&
                      programHeaderSubsectionTitle && (
                        <div
                          className="text-white text-mob italic sm:no-br"
                          dangerouslySetInnerHTML={{
                            __html: programHeaderSubsectionTitle
                          }}
                        />
                      )}
                  </div>

                  <div className="divide-y border-[.5px] border-gray-generic/30 mt-6 mb-3 sm:my-3" />
                  {includesList?.length > 0 && (
                    <CollapsibleListINGOWrapper
                      listItems={includesList}
                      containerClass="flex pt-3 sm:py-2 items-start  flex-1"
                      listContainerClass={
                        "flex flex-row flex-wrap gap-2 sm:gap-2"
                      }
                      listItemClass="flex flex-row gap-1 sm:gap-2 items-center"
                      displayPlain={false}
                      displayPlainCharCount={programIngo ? 100 : 150}
                      breakpoint={breakpoint}
                      showMore={false}
                      gviFlex={programIngo}
                      itemTextClass={itemTextClass}
                    />
                  )}
                </div>
              </div>

              <div
                className={`sm:w-1/4 flex flex-col pt-3 sm:pt-0 h-full justify-end sm:justify-normal sm:z-10 ${rightContainerSectionClass}`}
              >
                <div
                  className={`program-rating-container ${programRatingContainerClass}`}
                >
                  <ProgramRating
                    outerContainerClass={"relative items-center gap-2 w-full"}
                    ratingIconClass={"fill-white planet:fill-generic-text h-4"}
                    ratingLogoClass={"fill-white planet:fill-gray-lighter h-4"}
                    darkRatingIconClass={"block fill-white"}
                    darkContainerClass={"flex gap-2 items-center"}
                    ingoProgram={programIngo}
                  />
                </div>

                {!programIngo &&
                  programPopular &&
                  programCapacity &&
                  breakpoint === "xs" && (
                    <ProgramCapacity
                      programCapacityText={programCapacity}
                      outerContainerClass={"pt-2 sm:hidden"}
                      headerClass={
                        "inline flex flex-nowrap flex-shrink text-white text-xs xxs:text-xxxs"
                      }
                      alarmIconClass="w-4 h-4 fill-yellow-lighten planet:fill-green-standard"
                      megaPhoneIconClass="w-4 h-4 fill-yellow-lighten planet:fill-green-standard"
                      headerTitleClass={
                        "text-yellow-light planet:text-gray-generic text-xs"
                      }
                    />
                  )}

                <TravelFlexibility
                  travelDetails={travelDetails}
                  outerContainerClass="border-0 sm:h-fit planet:bg-transparent"
                  innerContainerClass={travelInnerContainerClass}
                  headingClass={`text-mob font-bold md:text-base md:font-normal hidden sm:block ${travelHeadingClass}`}
                  descriptionClass={`text-xs ${travelDescriptionClass}`}
                  ingo={programIngo}
                  isGVI={!programIngo}
                  hasBullets={false}
                  listItemClass="text-xs"
                />
              </div>
            </div>

            <div className="flex flex-col gap-6 sm:gap-2 sm:flex-row w-full pt-2 items-center">
              <div className={"flex flex-wrap w-full sm:w-1/2 gap-1"}>
                {!programIngo && programPopular && programCapacity && (
                  <div className={"w-full hidden sm:flex"}>
                    <ProgramCapacity
                      programCapacityText={programCapacity}
                      outerContainerClass={"pt-0"}
                      headerClass={"text-white"}
                      headerTitleClass={
                        "text-yellow-light planet:text-gray-generic"
                      }
                    />
                  </div>
                )}

                <div className="flex w-full gap-1 sm:gap-2 sm:items-center flex-col sm:flex-row">
                  {(program?.duration ||
                    program?.acfProgramsMeta?.durationDisplay) && (
                    <div
                      className={durationClass}
                      dangerouslySetInnerHTML={{
                        __html: `Durations: ${
                          program?.duration ??
                          program?.acfProgramsMeta?.durationDisplay
                        }`
                      }}
                    />
                  )}

                  {flexConfig?.showPricing &&
                    programTypeSlugs?.includes("volunteering") &&
                    minPriceFilterAmount && (
                      <>
                        <div className="hidden sm:block divide-y height-divider mt-1 h-4 border border-gray-ingo" />
                        <div
                          className={priceClass}
                          dangerouslySetInnerHTML={{
                            __html: `From ${minPrice} ${programIngo ? "per day" : ""}`
                          }}
                        />
                      </>
                    )}
                </div>
              </div>
              <div className="w-full sm:w-1/2">
                <div
                  className={`z-20 ${
                    type !== "apply" ? "md:w-2/5 min-w-40" : ""
                  }`}
                >
                  {type !== "apply" ? (
                    <ViewButton to={to} ctaViewLabel={ctaViewLabel} />
                  ) : (
                    <div className="flex justify-end items-end">
                      <div className="w-full flex flex-1 flex-col md:flex-row justify-end items-end gap-4 sm:gap-5">
                        {target === "application" && (
                          <CtaButton
                            preset={ctaViewPreset}
                            ctaLabel={ctaViewLabel}
                            to={program?.link ?? program?.uri}
                            extraButtonclassName={`planet:bg-transparent`}
                            extraButtonStyle={`w-full sm:w-86 planet:bg-transparent xxxs:rounded-full planet:xxxs:rounded-none sm:rounded-full ${ctaViewExtraClass}`}
                            labelClass={ctaViewLabelClass}
                            arrowClass={ctaViewArrowClass}
                          />
                        )}
                        {isProgramFocusPage && (
                          <CtaButton
                            preset={
                              target === "application" ? "primary" : "secondary"
                            }
                            ctaLabel={ctaApplyLabel}
                            labelClass={labelClass}
                            arrowClass={arrowClass}
                            extraButtonclassName={`${buttonClassName}`}
                            extraButtonStyle="w-full sm:w-86 flex xxxs:rounded-full planet:xxxs:rounded-none sm:rounded-full"
                            to={to}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgramApplyCard;
